if (!customElements.get('store-availability')) {
  customElements.define(
    'store-availability',
    class StoreAvailability extends HTMLElement {
      constructor() {
        super();

        this.abortController = undefined;
        this.sectionRenderId = null;
        this.optionNames = [];
        this.optionMap = {};
        this.errorHtml = this.querySelector('template')?.content?.cloneNode(true);
        this.onClickRefresh = this.onClickRefresh.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onDocumentVariantChange = this.onDocumentVariantChange.bind(this);
      }

      connectedCallback() {
        this.variantChangeUnsubscriber = window.FoxThemeEvents.subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.onVariantChange
        );
        document.addEventListener('variant:changed', this.onDocumentVariantChange);

        this.init();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        document.removeEventListener('variant:changed', this.onDocumentVariantChange);
        this.abortController?.abort();
      }

      async init() {
        this.parseOptionNames();
        this.setOptionValues(this.parseJsonArray(this.dataset.optionValues));
        this.sectionRenderId = await this.resolveSectionRenderId();
        this.fetchAvailability(this.dataset.variantId);
      }

      parseJsonArray(value) {
        try {
          const parsed = JSON.parse(value || '[]');
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          return [];
        }
      }

      parseOptionNames() {
        this.optionNames = this.parseJsonArray(this.dataset.optionNames);
      }

      getVariantOptionValues(variant) {
        if (Array.isArray(variant?.options) && variant.options.length) {
          return variant.options;
        }

        const values = [];

        if (variant?.option1 != null && variant.option1 !== '') {
          values.push(variant.option1);
        }

        if (variant?.option2 != null && variant.option2 !== '') {
          values.push(variant.option2);
        }

        if (variant?.option3 != null && variant.option3 !== '') {
          values.push(variant.option3);
        }

        return values;
      }

      setOptionValues(values) {
        this.optionMap = {};

        this.optionNames.forEach((name, index) => {
          if (!name) return;

          const value = values?.[index];

          if (value != null && value !== '') {
            this.optionMap[String(name).toLowerCase()] = String(value);
          }
        });
      }

      getSectionRenderCacheKey() {
        return `storeAvailabilitySectionRenderId:${this.dataset.productUrl || window.location.pathname}`;
      }

      async resolveSectionRenderId() {
        const cached = sessionStorage.getItem(this.getSectionRenderCacheKey());
        if (cached) return cached;

        const productUrl = this.dataset.productUrl;
        if (!productUrl) return 'store-availability';

        try {
          const response = await fetch(`${productUrl}?view=store-availability`, {
            cache: 'no-store',
          });

          if (!response.ok) return 'store-availability';

          const text = await response.text();
          const match = text.match(/id="shopify-section-(template--\d+__store-availability)"/);

          if (match?.[1]) {
            sessionStorage.setItem(this.getSectionRenderCacheKey(), match[1]);
            return match[1];
          }
        } catch (error) {
          console.error(error);
        }

        return 'store-availability';
      }

      onVariantChange({ data: { variant, sectionId } = {} }) {
        if (!sectionId || sectionId !== this.dataset.sectionId) return;

        this.update(variant);
      }

      onDocumentVariantChange(event) {
        this.update(event.detail?.variant);
      }

      getStockMessages() {
        return {
          high: this.dataset.stockMessageHigh || '',
          low: this.dataset.stockMessageLow || '',
          out: this.dataset.stockMessageOut || '',
        };
      }

      formatStockMessage(message, quantity) {
        return (message || '').replace(/\[([^\]]+)\]/g, (_, token) => {
          const key = token.toLowerCase();

          if (key === 'quantity') {
            return String(quantity);
          }

          return this.optionMap[key] ?? '';
        });
      }

      getStockTier(quantity) {
        const threshold = Number(this.dataset.lowStockThreshold) || 4;

        if (quantity === 0) {
          return 'out';
        }

        if (quantity > threshold) {
          return 'high';
        }

        return 'low';
      }

      applyStockLabels(container) {
        if (!container) return;

        const messages = this.getStockMessages();

        container.querySelectorAll('[data-store-qty]').forEach((item) => {
          const quantity = Number(item.dataset.storeQty) || 0;
          const tier = this.getStockTier(quantity);
          const dot = item.querySelector('.store-availability-list__dot');
          const textEl = item.querySelector('[data-store-availability-stock-text]');

          if (!dot || !textEl) return;

          dot.className = `store-availability-list__dot store-availability-list__dot--${tier}`;
          textEl.textContent = this.formatStockMessage(messages[tier], quantity);
        });

        const emptyEl = container.querySelector('[data-empty-type="config"]');
        if (emptyEl && this.dataset.emptyMessage) {
          emptyEl.textContent = this.dataset.emptyMessage;
        }
      }

      buildFetchUrl(variantId) {
        if (!variantId) return null;

        const root = window.FoxThemeSettings?.routes?.root || window.Shopify?.routes?.root || '/';
        const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
        const url = new URL(`${normalizedRoot}variants/${variantId}/`, window.location.origin);

        url.searchParams.set('view', 'store-availability');
        url.searchParams.set('section_id', this.sectionRenderId || 'store-availability');
        return url.toString();
      }

      fetchAvailability(variantId) {
        const runFetch = () => {
          const requestUrl = this.buildFetchUrl(variantId);
          const container = this.querySelector('[data-store-availability-drawer-content]');

          if (!requestUrl || !container) {
            return;
          }

          this.abortController?.abort();
          this.abortController = new AbortController();

          fetch(requestUrl, {
            signal: this.abortController.signal,
            cache: 'no-store',
          })
            .then((response) => {
              if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
              }
              return response.text();
            })
            .then((text) => {
              const html = new DOMParser().parseFromString(text, 'text/html');
              const content =
                html.querySelector('#StoreAvailabilityContent') ||
                html.querySelector('[data-store-availability-content]') ||
                html.querySelector('.section-store-availability');

              if (!content) {
                throw new Error('Missing store availability content');
              }

              container.innerHTML = content.innerHTML;
              this.applyStockLabels(container);
            })
            .catch((error) => {
              if (error.name === 'AbortError') return;
              this.renderError(container);
            });
        };

        if (this.sectionRenderId) {
          runFetch();
          return;
        }

        this.resolveSectionRenderId().then((sectionRenderId) => {
          this.sectionRenderId = sectionRenderId;
          runFetch();
        });
      }

      renderError(container) {
        if (!this.errorHtml || !container) return;

        container.replaceChildren(...this.errorHtml.childNodes);

        container.querySelector('[data-store-availability-refresh]')?.addEventListener('click', this.onClickRefresh);
      }

      onClickRefresh(event) {
        event.preventDefault();
        this.fetchAvailability(this.dataset.variantId);
      }

      update(variant) {
        if (!variant?.id) return;

        this.setOptionValues(this.getVariantOptionValues(variant));
        this.dataset.variantId = String(variant.id);
        this.fetchAvailability(variant.id);
      }
    }
  );
}
